import { useEffect, useState } from 'react'
import { inject, observer } from 'mobx-react'
import { useLocation } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import { Button, Spinner } from '@chakra-ui/react'
import { useMessage } from 'hooks/ToastMessage'
import { Logo, Circle } from 'icon'
import { openCustomDeepLink, openDeepLink } from 'utils'
import Keyboard from './Keyboard'
import Background from '../../assets/images/bg-pin.png'

const PinPage = (props) => {
  const location = useLocation()
  const theme = useTheme()
  const toastMessage = useMessage()

  const [loading, setLoading] = useState(false)
  const [codeList, setCodeList] = useState(['', '', '', '', '', ''])
  const [wrongCount, setWrongCount] = useState(0)

  const { language } = props
  const index = codeList.findIndex((it) => it === '')

  useEffect(() => {
    if (location?.state?.lang) {
      props.language.setLanguage(location?.state?.lang || 'th')
    }
  }, [location.state, props.language])

  const makeError = (msg) => {
    if (wrongCount < 2) {
      setWrongCount(wrongCount + 1)
      const errorMessage = language.getMsg(
        'The PIN code is incorrect, try again.',
        'รหัส PIN ที่กรอกไม่ถูกต้อง ลองใหม่อีกครั้ง'
      )
      toastMessage({ title: errorMessage })
    } else {
      setWrongCount(0)
      const errorMessage = language.getMsg(
        'The PIN code is incorrect. Please set a new PIN code.',
        'รหัส PIN ไม่ถูกต้อง กรุณาตั้งรหัสใหม่อีกครั้ง'
      )
      toastMessage({ title: errorMessage })
      openDeepLink('invalidPinCode')
    }

    setCodeList(['', '', '', '', '', ''])
  }

  const onSubmit = async () => {
    setLoading(true)

    const state = location?.state || {}

    const data = {
      // token: cookie.otpToken,
      token: state.otpToken,
      pinCode: codeList.join(''),
      redirectUri: window.location.origin, // state.redirectUri,
      clientId: state.clientId,
      state: state.state,
      codeChallenge: state.codeChallenge,
    }

    try {
      const loginPinCodeResponse = await props.otp.loginPinCode(data)
      if (loginPinCodeResponse) {
        setLoading(false)
        openCustomDeepLink(loginPinCodeResponse.request.responseURL)
      } else {
        makeError()
      }
    } catch (e) {
      setLoading(false)
      makeError(e.message)
    }
  }

  const onBack = () => {
    openDeepLink('verifyAccount')
  }

  const onKeyboard = (val) => {
    if (index === -1) return

    const len = codeList.length
    if (index < len) {
      codeList[index] = val
      setCodeList([...codeList])
    }

    if (index === len - 1) {
      onSubmit()
    }
  }

  const onDelete = () => {
    const i = index === -1 ? codeList.length - 1 : index - 1
    if (i >= 0) {
      codeList[i] = ''
      setCodeList([...codeList])
    }
  }

  const codes = codeList.map((it, i) => {
    const isActive = it !== ''
    const c = isActive ? <Circle /> : ''
    return <Code key={i}>{c}</Code>
  })

  // url('/bedee-web/bg-pin.png')
  // <PageView style={{ backgroundImage: "url('/bg-pin.png')" }}>
  // background-image: 'url(${Background})'
  // <PageView style={{ backgroundImage: `url(${Background})` }}>
  return (
    <BackgroundContainer>
      <PageView>
        <Logo />
        <Title>{language.getMsg('Welcome back', 'ยินดีต้อนรับ')}</Title>
        <Desc>
          {language.getMsg(
            'Enter your 6-digit PIN code.',
            'กรอกรหัส PIN 6 หลัก เพื่อเข้าใช้งาน'
          )}
        </Desc>

        {loading ? (
          <SpinnerContainer>
            <Spinner />
          </SpinnerContainer>
        ) : (
          <CodeSection>{codes}</CodeSection>
        )}
        <Keyboard onClick={onKeyboard} onDelete={onDelete} />
        <ForgetContainer>
          <Button
            color={theme.color.darkBlue}
            fontSize={16}
            fontWeight={600}
            onClick={onBack}
            variant="link"
          >
            {language.getMsg('Forgot PIN code?', 'ลืมรหัส PIN?')}
          </Button>
        </ForgetContainer>
      </PageView>
    </BackgroundContainer>
  )
}

const BackgroundContainer = styled.div`
  width: 100%;
  background-color: #eaf1ff;
`
const PageView = styled.div`
  width: 100%;
  min-height: 100vh;
  max-width: 480px;
  margin: 0 auto;
  padding: 24px 20px 20px 20px;
  position: relative;
  text-align: center;
  background-color: #eaf1ff;
  background-image: ${`url(${Background})`};

  svg {
    margin: 0 auto;
  }

  ${(p) => p.theme.media.mobile_xs} {
    padding: 24px 10px;
  }
`

const Title = styled.div`
  font-weight: 600;
  font-size: 24px;
  line-height: 35px;
  color: #3a4248;
  padding-top: 16px;

  ${(p) => p.theme.media.mobile_xs} {
    font-size: 25px;
  }
`

const Desc = styled.div`
  font-weight: 400;
  font-size: 14px;
  line-height: 18px;
  color: #555454;
  padding-top: 8px;
`

const CodeSection = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  column-gap: 8px;
  padding: 20px 0;

  ${(p) => p.theme.media.mobile_xs} {
    column-gap: 6px;
  }
`

const Code = styled.div`
  width: 48px;
  height: 48px;
  font-weight: 400;
  font-size: 24px;
  line-height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;

  color: #1f1f1f;
  background-color: #f7f8f9;
  border: 1px solid #e8ecf4;
  border-radius: 8px;

  &.active {
    font-weight: 600;
    color: #176afc;
    border: 1px solid #176afc;
  }

  ${(p) => p.theme.media.mobile_xs} {
    width: 45px;
    height: 45px;
  }
`

const ForgetContainer = styled.div`
  width: 100%;
  font-weight: 600;
  font-size: 15px;
  line-height: 22px;
  text-align: center;
  color: #07236d;
  padding-top: 40px;
`
const SpinnerContainer = styled.div`
  padding: 28.3px 0;
`

export default inject('otp', 'language')(observer(PinPage))
